import {useState, useRef, useEffect} from 'react';
import {useNavigate} from 'react-router-dom';
import {User, LogOut, ChevronDown} from 'lucide-react';
import {useAppContext} from '@/matic/providers/AppProvider';
import {signOut} from '@/matic/lib/auth/sign-out';
import {useSidebarStore} from './sidebar/sidebarStore';

// ============================================================================
// Helper functions
// ============================================================================

function getUserInitials(name?: string, email?: string) {
  if (name) {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  }

  if (email) {
    return email.slice(0, 2).toUpperCase();
  }

  return 'U';
}

function getUserDisplayName(name?: string, email?: string) {
  if (name) return name;
  if (email) return email.split('@')[0];
  return 'User';
}

function getDropdownClasses(isSidebar: boolean, collapsed: boolean) {
  if (isSidebar) {
    const base =
      'absolute z-50 w-full rounded-lg border border-primary-500 bg-sidebar-bg py-2 shadow-lg';
    return collapsed
      ? `${base} left-full bottom-0 ml-2 w-64`
      : `${base} bottom-full left-0 mb-2`;
  }

  return 'absolute z-50 w-64 rounded-lg border border-neutral-200 bg-white py-2 shadow-lg right-0 mt-2';
}

// ============================================================================
// Sub-components
// ============================================================================

type UserProfileDropdownProps = {
  isSidebar: boolean;
  collapsed: boolean;
  userName?: string;
  userEmail?: string;
  onProfileClick: () => void;
  onSignOut: () => void;
};

function UserProfileDropdown({
  isSidebar,
  collapsed,
  userName,
  userEmail,
  onProfileClick,
  onSignOut,
}: UserProfileDropdownProps) {
  const dropdownClasses = getDropdownClasses(isSidebar, collapsed);

  return (
    <div className={dropdownClasses}>
      {/* User Info Header */}
      <div
        className={`border-b px-4 py-3 ${isSidebar ? 'border-primary-500/30' : 'border-neutral-100'}`}
      >
        <p
          className={`truncate text-sm font-medium ${isSidebar ? 'text-white' : 'text-neutral-900'}`}
        >
          {userName ?? 'User'}
        </p>
        <p
          className={`truncate text-xs ${isSidebar ? 'text-gray-400' : 'text-neutral-500'}`}
        >
          {userEmail}
        </p>
      </div>

      {/* Menu Items */}
      <div className="py-1">
        <button
          onClick={onProfileClick}
          className={`flex w-full items-center px-4 py-2 text-sm transition-colors ${isSidebar ? 'text-white hover:bg-white/10' : 'text-neutral-700 hover:bg-neutral-50'}`}
        >
          <User className="mr-3 h-4 w-4" />
          View Profile
        </button>
      </div>

      {/* Sign Out */}
      <div
        className={`border-t py-1 ${isSidebar ? 'border-primary-500/30' : 'border-neutral-100'}`}
      >
        <button
          onClick={onSignOut}
          className={`flex w-full items-center px-4 py-2 text-sm transition-colors ${isSidebar ? 'text-white hover:bg-white/10 hover:text-primary-400' : 'text-neutral-900 hover:bg-primary-50 hover:text-primary-600'}`}
        >
          <LogOut className="mr-3 h-4 w-4" />
          Sign Out
        </button>
      </div>
    </div>
  );
}

// ============================================================================
// Main component
// ============================================================================

export default function UserProfile({
  variant = 'header',
  collapsed = false,
}: {
  variant?: 'header' | 'sidebar';
  collapsed?: boolean;
}) {
  const {user, isLoading} = useAppContext();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const openSidebar = useSidebarStore((state) => state.open);
  const isProfileOpen = useSidebarStore((state) => state.isProfileOpen);
  const setProfileOpen = useSidebarStore((state) => state.setProfileOpen);

  const isSidebar = variant === 'sidebar';
  const showDropdown = isSidebar ? isProfileOpen : isOpen;

  const closeDropdown = () => {
    if (isSidebar) {
      setProfileOpen(false);
    } else {
      setIsOpen(false);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        closeDropdown();
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSignOut = () => {
    closeDropdown();
    signOut();
  };

  const handleProfileClick = () => {
    closeDropdown();
    navigate('/profile');
  };

  if (isLoading) {
    return (
      <div className="flex items-center space-x-2">
        <div className="h-8 w-8 animate-pulse rounded-full bg-neutral-200" />
        {!collapsed && (
          <div className="h-4 w-20 animate-pulse rounded bg-neutral-200" />
        )}
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex items-center space-x-2">
        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-neutral-300">
          <span className="text-sm font-medium text-neutral-600">?</span>
        </div>
        {!collapsed && (
          <span className="text-sm text-neutral-500">Not signed in</span>
        )}
      </div>
    );
  }

  const textColor = isSidebar ? 'text-white' : 'text-neutral-700';
  const hoverBg = isSidebar ? 'hover:bg-sidebar-hover' : 'hover:bg-neutral-100';
  const chevronColor = isSidebar ? 'text-gray-400' : 'text-neutral-500';
  const avatarSize = isSidebar ? 'h-7 w-7' : 'h-8 w-8';
  const avatarTextSize = isSidebar ? 'text-xs' : 'text-sm';

  return (
    <div className={`relative ${isSidebar ? 'w-full' : ''}`} ref={dropdownRef}>
      <button
        aria-label="Open user menu"
        aria-haspopup="menu"
        aria-expanded={showDropdown}
        onClick={() => {
          if (collapsed) {
            openSidebar();
            setProfileOpen(true);
          } else if (isSidebar) {
            setProfileOpen(!isProfileOpen);
          } else {
            setIsOpen(!isOpen);
          }
        }}
        className={`flex items-center space-x-2 rounded-md px-2 py-1 transition-colors ${hoverBg} ${isSidebar ? 'w-full' : ''} ${collapsed ? 'justify-center' : ''}`}
      >
        <div
          className={`flex ${avatarSize} shrink-0 items-center justify-center rounded-full bg-primary-500`}
        >
          <span className={`${avatarTextSize} font-medium text-white`}>
            {getUserInitials(user.name, user.email)}
          </span>
        </div>
        {!collapsed && (
          <div className="flex min-w-0 flex-1 items-center justify-between space-x-1">
            <span className={`truncate text-xs font-medium ${textColor}`}>
              {getUserDisplayName(user.name, user.email)}
            </span>
            <ChevronDown
              className={`h-4 w-4 transition-transform ${chevronColor} ${showDropdown ? 'rotate-180' : ''}`}
            />
          </div>
        )}
      </button>

      {showDropdown && (
        <UserProfileDropdown
          isSidebar={isSidebar}
          collapsed={collapsed}
          userName={user.name}
          userEmail={user.email}
          onProfileClick={handleProfileClick}
          onSignOut={handleSignOut}
        />
      )}
    </div>
  );
}
